import React, { useEffect, useRef } from 'react';

type MapSearchBoxProps = {
  map: google.maps.Map;
};

const MapSearchBox: React.FC<MapSearchBoxProps> = ({ map }) => {
  const searchInputRef = useRef<any>(null);
  const searchBoxRef = useRef<any>(null);

  useEffect(() => {
    if (!searchInputRef?.current) return;

    let intervalId: any;

    const initializeSearchBox = (googleObj: any) => {
      // Guard against double init
      if (searchBoxRef.current || !searchInputRef.current) return;

      const newSearchBox: any = new googleObj.maps.places.SearchBox(
        searchInputRef.current
      );
      searchBoxRef.current = newSearchBox;

      const onPlacesChanged = () => {
        const selected = newSearchBox.getPlaces();
        if (!selected || selected.length === 0) return;

        const place: any = selected[0];
        if (!place?.geometry) return;

        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.setCenter(place.geometry.location);
          map.setZoom(6);
        }

        searchInputRef.current!.blur();
      };

      newSearchBox.addListener('places_changed', onPlacesChanged);
    };

    const googleObj: any = (window as any).google;
    if (googleObj?.maps?.places?.SearchBox) {
      initializeSearchBox(googleObj);
    } else {
      // In rare cases the Places library may not yet be available even when the map is loaded.
      // Poll briefly until it is ready to avoid runtime errors on first load.
      intervalId = setInterval(() => {
        const g: any = (window as any).google;
        if (g?.maps?.places?.SearchBox) {
          clearInterval(intervalId);
          initializeSearchBox(g);
        }
      }, 100);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (searchBoxRef.current && (window as any).google?.maps?.event) {
        (window as any).google.maps.event.clearInstanceListeners(
          searchBoxRef.current
        );
      }
      searchBoxRef.current = null;
    };
  }, [map]);

  return (
    <div>
      <form className="w-full py-2" onSubmit={(e) => e.preventDefault()}>
        <label
          htmlFor="default-search"
          className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
        >
          Search
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            id="default-search"
            className="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Search places..."
            required
          />
        </div>
      </form>
    </div>
  );
};

export default MapSearchBox;
